import React, { useRef, useEffect } from "react";
import { Typography } from "antd";
import { ValuationSnapshot } from "./sections/ValuationSnapshot";
import { QuarterlyFinancials } from "./sections/QuarterlyFinancials";
import { AnnualOutlook } from "./sections/AnnualOutlook";
import { AnnualHistory } from "./sections/AnnualHistory";
import { Dividends } from "./sections/Dividends";
import { IncomeStatement } from "./sections/IncomeStatement";
import { BalanceSheet } from "./sections/BalanceSheet";
import { CashFlow } from "./sections/CashFlow";
import { Statistics } from "./sections/Statistics";

const { Text } = Typography;

interface FinancialsTabProps {
  sectorPeers: any[];
  baseCurrency: string;
  selectedStock: any;
}

const sections = [
  { key: "valuation", label: "Valuation Snapshot" },
  { key: "quarterly", label: "Quarterly Financials" },
  { key: "outlook", label: "Annual Outlook" },
  { key: "history", label: "Annual History" },
  { key: "dividends", label: "Dividends" },
  { key: "income", label: "Income Statement" },
  { key: "balance", label: "Balance Sheet" },
  { key: "cashflow", label: "Cash Flow" },
  { key: "statistics", label: "Statistics" },
];

export const FinancialsTab: React.FC<FinancialsTabProps> = ({
  sectorPeers,
  baseCurrency,
  selectedStock,
}) => {
  const [activeSection, setActiveSection] = React.useState("valuation");
  const navRef = useRef<HTMLDivElement>(null);

  // Height of the page's own sticky top-level tab bar (Company Profile/
  // Charts/Financials/Peers — StockAnalysisTabs.tsx) that sits ABOVE this
  // section's own sticky sub-nav. Used to be a hardcoded `52` repeated in
  // three places below — that broke the moment the main tab bar's own
  // padding changed (it's a separate component, so nothing here could
  // know its new height), pushing this sub-nav under it when both stick
  // to the top on scroll. Measured from the live DOM instead, so a
  // future change to that bar's height doesn't silently desync this one
  // again. 52 stays only as the fallback for the brief window before
  // the first measurement (and if the element is ever not found).
  const [mainTabBarHeight, setMainTabBarHeight] = React.useState(52);

  useEffect(() => {
    const measure = () => {
      const el = document.querySelector<HTMLElement>(
        ".vi-ssa-main-tabs .ant-tabs-nav",
      );
      if (el) setMainTabBarHeight(el.getBoundingClientRect().height);
    };

    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  // Guard so the observer doesn't fight the smooth-scroll triggered by a click
  const isClickScrolling = useRef(false);
  const clickScrollTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const refs: Record<string, React.RefObject<HTMLDivElement>> = {
    valuation: useRef<HTMLDivElement>(null),
    quarterly: useRef<HTMLDivElement>(null),
    outlook: useRef<HTMLDivElement>(null),
    history: useRef<HTMLDivElement>(null),
    dividends: useRef<HTMLDivElement>(null),
    income: useRef<HTMLDivElement>(null),
    balance: useRef<HTMLDivElement>(null),
    cashflow: useRef<HTMLDivElement>(null),
    statistics: useRef<HTMLDivElement>(null),
  };

  const scrollTo = (key: string) => {
    setActiveSection(key);
    const el = refs[key]?.current;
    if (!el) return;

    // Pause the observer while we programmatically scroll
    isClickScrolling.current = true;
    if (clickScrollTimeout.current) clearTimeout(clickScrollTimeout.current);

    const offset =
      (navRef.current?.getBoundingClientRect().height ?? 41) +
      mainTabBarHeight +
      16;
    el.style.scrollMarginTop = `${offset}px`;
    el.scrollIntoView({ behavior: "smooth", block: "start" });

    // Smooth scroll duration is unpredictable, so give it a generous window
    // before handing control back to the observer
    clickScrollTimeout.current = setTimeout(() => {
      isClickScrolling.current = false;
    }, 700);
  };

  useEffect(() => {
    const navHeight = navRef.current?.getBoundingClientRect().height ?? 41;
    const topOffset = navHeight + mainTabBarHeight + 16;

    const observer = new IntersectionObserver(
      (entries) => {
        if (isClickScrolling.current) return;

        // Pick the entry closest to the top of the "active zone" among
        // those currently intersecting
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

        if (visible.length > 0) {
          const key = (visible[0].target as HTMLElement).dataset.sectionKey;
          if (key) setActiveSection(key);
        }
      },
      {
        // Treat a horizontal band just below the sticky nav as the
        // "trigger line" for switching tabs
        root: null,
        rootMargin: `-${topOffset}px 0px -70% 0px`,
        threshold: 0,
      },
    );

    sections.forEach((s) => {
      const el = refs[s.key]?.current;
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
    // mainTabBarHeight starts at the 52 fallback and gets corrected
    // shortly after mount by the measurement effect above — this needs
    // to re-run when that happens, or the observer's trigger line stays
    // pinned to the stale fallback value forever.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainTabBarHeight]);

  return (
    <div className="flex flex-col w-full">
      {/* Sticky sub-nav */}
      <div
        ref={navRef}
        className="sticky z-10 bg-white border-b border-gray-100 flex gap-2 overflow-x-auto py-2 mb-4 scrollbar-hide"
        style={{ top: mainTabBarHeight }}
      >
        {sections.map((s) => (
          <button
            key={s.key}
            onClick={() => scrollTo(s.key)}
            className={`whitespace-nowrap px-3 py-1 rounded-full text-xs font-medium transition-colors ${
              activeSection === s.key
                ? "bg-blue-600 text-white"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      <div className="flex flex-col gap-10">
        <div ref={refs.valuation} data-section-key="valuation">
          <ValuationSnapshot
            selectedStock={selectedStock}
            baseCurrency={baseCurrency}
            sectorPeers={sectorPeers}
          />
        </div>
        <div ref={refs.quarterly} data-section-key="quarterly">
          <QuarterlyFinancials
            baseCurrency={baseCurrency}
            selectedStock={selectedStock}
          />
        </div>
        <div ref={refs.outlook} data-section-key="outlook">
          <AnnualOutlook
            baseCurrency={baseCurrency}
            selectedStock={selectedStock}
          />
        </div>
        <div ref={refs.history} data-section-key="history">
          <AnnualHistory
            baseCurrency={baseCurrency}
            selectedStock={selectedStock}
          />
        </div>
        <div ref={refs.dividends} data-section-key="dividends">
          <Dividends
            baseCurrency={baseCurrency}
            selectedStock={selectedStock}
          />
        </div>
        <div ref={refs.income} data-section-key="income">
          <IncomeStatement selectedStock={selectedStock} />
        </div>
        <div ref={refs.balance} data-section-key="balance">
          <BalanceSheet selectedStock={selectedStock} />
        </div>
        <div ref={refs.cashflow} data-section-key="cashflow">
          <CashFlow selectedStock={selectedStock} />
        </div>
        <div ref={refs.statistics} data-section-key="statistics">
          <Statistics selectedStock={selectedStock} />
        </div>
      </div>
    </div>
  );
};
